import { ACTION_REGISTRY } from '../../domain/accessRegistry';
import { resolveChapterAccessAction } from '../../domain/access/chapterActionPolicy';
import { isGithubDeepDiveSource, parseStoredTopicSource } from '../../domain/topicSourcePolicy';
import type { AccessAction } from '../../domain/types/accessTypes';
import { resolveClientAccess, type ClientAccessDecision } from './accessResolver';
import { PrototypeApiError } from './apiTransport';
import { playbackErrorCode } from './mediaChapterTransitionCoordinator';
import { presentAccessDecision } from './presentAccessDecision';
import type { PrototypeState } from './controllerState';
import type { MediaKind } from './mediaPlayerTypes';
import type { Root } from './prototypeTypes';
import { getTopic } from './topicStore';

type DynamicPaywallCapability = 'githubDeepDive';

export function resolveMediaAccessDecision(
	root: Root,
	kind: MediaKind,
	topicId: string,
	chapterIndex: number,
): ClientAccessDecision {
	const state = readPrototypeState(root);
	if (!state) return { ok: true, kind: 'allow', action: mediaAction(kind) };
	return resolveMediaAccessDecisionForState(root, state, kind, topicId, chapterIndex);
}

export function resolveMediaAccessDecisionForState(
	root: Root,
	state: PrototypeState,
	kind: MediaKind,
	topicId: string,
	chapterIndex: number,
): ClientAccessDecision {
	const action = mediaAction(kind);
	const topic = getTopic(topicId);
	const source = topic?.source ?? currentPageModelTopicSource(root, topicId);
	const knownPublicTopic = topic?.isPrivate === false || (source !== undefined && source !== 'private_ai');
	const context = {
		isLoggedIn: state.isLoggedIn,
		plan: state.effectiveAccessPlan,
		resource: {
			type: 'chapter',
			index: knownPublicTopic ? chapterIndex : undefined,
		},
	} as const;
	const chapterAction = source ? resolveChapterAccessAction(source) : null;
	if (chapterAction && ACTION_REGISTRY[chapterAction].fullAccessCapability) {
		const chapterDecision = resolveClientAccess(chapterAction, context);
		if (chapterDecision.kind !== 'allow') return mediaGateFromChapterDecision(chapterAction, chapterDecision);
	}
	return resolveClientAccess(action, context);
}

export function presentMediaAccessDecision(
	root: Root,
	decision: ClientAccessDecision,
	rawAction: string,
	requiredPlan?: 'lite' | 'pro' | 'max',
	dynamicRequiredCapability?: DynamicPaywallCapability,
): boolean {
	if (decision.kind === 'allow') return false;
	const state = readPrototypeState(root);
	if (!state) return false;
	return presentAccessDecision(root, state, decision, {
		rawAction,
		policyAction: decision.action,
		...(requiredPlan ? { requiredPlan } : {}),
		...(dynamicRequiredCapability ? { dynamicRequiredCapability } : {}),
	});
}

export function presentPlaybackAccessError(root: Root, kind: MediaKind, error: unknown, topicId?: string): boolean {
	if (!(error instanceof PrototypeApiError)) return false;
	const code = playbackErrorCode(error);
	if (code !== 'authentication_required' && code !== 'entitlement_required') return false;
	const state = readPrototypeState(root);
	if (!state) return false;
	const action = mediaAction(kind);
	const decision: ClientAccessDecision = code === 'authentication_required'
		? { ok: false, kind: 'login', code, action }
		: { ok: false, kind: 'paywall', code, action, capability: 'publicMediaAccess' };
	return presentMediaAccessDecision(root, decision, 'media-playback', requiredPlanFromPlaybackError(error), dynamicPaywallCapabilityForPlaybackTarget(root, topicId));
}

function mediaAction(kind: MediaKind): Extract<AccessAction, 'media.audio.play' | 'media.video.play'> {
	return kind === 'audio' ? 'media.audio.play' : 'media.video.play';
}

function readPrototypeState(root: Root): PrototypeState | null {
	return (root as unknown as { __prototypeState?: PrototypeState }).__prototypeState ?? null;
}

function mediaGateFromChapterDecision(action: AccessAction, decision: ClientAccessDecision): ClientAccessDecision {
	if (decision.kind !== 'partial') return decision;
	const capability = ACTION_REGISTRY[action].fullAccessCapability;
	if (!capability) return decision;
	if (decision.gate === 'login') return { ok: false, kind: 'login', code: 'authentication_required', action };
	return { ok: false, kind: 'paywall', code: 'entitlement_required', action, capability };
}

function requiredPlanFromPlaybackError(error: PrototypeApiError): 'lite' | 'pro' | 'max' | undefined {
	const requiredPlan = error.body && typeof error.body === 'object' && 'requiredPlan' in error.body
		? (error.body as { requiredPlan?: unknown }).requiredPlan
		: undefined;
	return requiredPlan === 'lite' || requiredPlan === 'pro' || requiredPlan === 'max' ? requiredPlan : undefined;
}

function currentPageModelTopicSource(root: Root, topicId: string) {
	const raw = root.querySelector<HTMLScriptElement>('#page-model')?.textContent;
	if (!raw) return undefined;
	try {
		const model = JSON.parse(raw) as { topic?: { id?: unknown; slug?: unknown; source?: unknown } };
		const topic = model?.topic;
		const matches = topic?.id === topicId || topic?.slug === topicId;
		return matches ? parseStoredTopicSource(topic?.source) ?? undefined : undefined;
	} catch {
		return undefined;
	}
}

function dynamicPaywallCapabilityForPlaybackTarget(root: Root, topicId: string | undefined): DynamicPaywallCapability | undefined {
	if (!topicId) return undefined;
	const source = getTopic(topicId)?.source ?? currentPageModelTopicSource(root, topicId);
	return isGithubDeepDiveSource(source) ? 'githubDeepDive' : undefined;
}
